import { useState, useMemo } from 'react'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { Icon, IconName, Typography } from '@themenu/design-system'

import { cn } from 'utils'

import Authorize, { PERMISSIONS } from 'components/Authorize'

import {
  GroupItemButton,
  type GroupItemButtonProps
} from './components/GroupItemButton'

export type GroupItemProps = {
  group_name: string
  group_permissions: PERMISSIONS[]
  icon: string
  group_list?: GroupItemButtonProps[]
  href?: string
  id?: string
  target_new?: boolean
  action?: () => void
}

export const GroupItem = ({
  group_name,
  icon,
  group_list,
  group_permissions,
  href,
  id,
  target_new = false
}: GroupItemProps) => {
  const { route } = useRouter()

  const [isOpen, setIsOpen] = useState(() => {
    if (!group_list) {
      return false
    }

    return group_list.some((item) => item.href === route)
  })

  const isActiveRoute = useMemo(() => {
    if (!href) {
      return false
    }

    return route.startsWith(href)
  }, [href, route])

  const handleToggle = () => {
    setIsOpen((prev) => !prev)
  }

  if (href) {
    return (
      <Authorize permissions={group_permissions}>
        <Link
          id={id}
          className={cn(
            'flex items-center gap-12 px-14 py-8 mb-4 rounded-8 hover:bg-background-default',
            isActiveRoute ? 'bg-background-default' : ''
          )}
          href={href}
          target={target_new ? '_blank' : undefined}
        >
          <Icon
            name={icon as IconName}
            size={16}
            className={`mb-[2px]
                ${
                  isActiveRoute
                    ? 'svg-children:stroke-content-primary svg-children:fill-content-primary'
                    : ''
                }`}
          />
          <Typography
            variant="callout"
            className={isActiveRoute ? 'text-content-primary' : ''}
          >
            {group_name}
          </Typography>
        </Link>
      </Authorize>
    )
  }

  return (
    <div
      data-testid={`${group_name}-container`}
      aria-expanded={isOpen}
      className={cn(
        'overflow-hidden transition-all duration-300 ease-in-out',
        'min-h-[40px]',
        isOpen ? 'max-h-[200px]' : 'max-h-[40px]'
      )}
    >
      <Authorize permissions={group_permissions}>
        <button
          onClick={handleToggle}
          className={cn('w-full flex items-center gap-12 px-14 py-8 rounded-8')}
        >
          <Icon name={icon as IconName} size={16} />
          <Typography variant="callout">{group_name}</Typography>
          <Icon
            name="chevron-down"
            size={16}
            className={`ml-auto transition-transform duration-300 ease-in-out ${
              isOpen ? '-rotate-180' : 'rotate-0'
            }`}
          />
        </button>
      </Authorize>
      {group_list && group_list.length > 0 && (
        <ul
          className={`
          list-none transition-opacity duration-300 ease-in-out
          ${
            isOpen
              ? 'opacity-100 pointer-events-auto'
              : 'opacity-0 pointer-events-none'
          }
        `}
        >
          {group_list.map((props) => (
            <Authorize key={props.label} permissions={props.permission}>
              <GroupItemButton {...props} />
            </Authorize>
          ))}
        </ul>
      )}
    </div>
  )
}
